import { useState } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNotify } from 'react-admin';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface JsonContentViewerProps {
  /** The content to display - can be a JSON string, object, or plain text */
  content: string | Record<string, unknown> | null;
  /** Whether to enable JSONify by default */
  defaultPretty?: boolean;
  /** Whether to show the toolbar with JSONify toggle and copy button */
  showToolbar?: boolean;
  /** Maximum height for the viewer (defaults to 100%) */
  maxHeight?: string | number;
}

/**
 * A reusable component for displaying JSON content with syntax highlighting.
 * Includes a JSONify toggle to parse nested JSON strings and a copy button.
 */
export const JsonContentViewer = ({
  content,
  defaultPretty = false,
  showToolbar = true,
  maxHeight = '100%',
}: JsonContentViewerProps) => {
  const [prettyJsonEnabled, setPrettyJsonEnabled] = useState(defaultPretty);
  const notify = useNotify();

  // Check if content is raw text (not JSON)
  const isRawText =
    content !== null && typeof content === 'object' && 'rawText' in content;

  // Process JSON recursively to parse escaped JSON strings and convert escape sequences
  const processJsonRecursively = (obj: unknown): unknown => {
    if (typeof obj === 'string') {
      try {
        const parsed = JSON.parse(obj);
        return processJsonRecursively(parsed);
      } catch {
        // If not valid JSON, process escape sequences for better readability
        return obj
          .replace(/\\n/g, '\n')
          .replace(/\\"/g, '"')
          .replace(/\\t/g, '\t')
          .replace(/\\r/g, '\r')
          .replace(/\\\\/g, '\\');
      }
    } else if (Array.isArray(obj)) {
      return obj.map(processJsonRecursively);
    } else if (obj !== null && typeof obj === 'object') {
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = processJsonRecursively(value);
      }
      return result;
    }
    return obj;
  };

  // Custom JSON stringifier that preserves converted newlines
  const customJsonStringify = (obj: unknown, indent: number = 0): string => {
    const spaces = '  '.repeat(indent);
    const nextSpaces = '  '.repeat(indent + 1);

    if (obj === null) return 'null';
    if (typeof obj === 'boolean' || typeof obj === 'number') return String(obj);
    if (typeof obj === 'string') {
      // Don't escape newlines and quotes that we've already converted
      return `"${obj}"`;
    }
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '[]';
      const items = obj
        .map(item => nextSpaces + customJsonStringify(item, indent + 1))
        .join(',\n');
      return `[\n${items}\n${spaces}]`;
    }
    if (typeof obj === 'object') {
      const keys = Object.keys(obj as Record<string, unknown>);
      if (keys.length === 0) return '{}';
      const items = keys
        .map(
          key =>
            `${nextSpaces}"${key}": ${customJsonStringify((obj as Record<string, unknown>)[key], indent + 1)}`
        )
        .join(',\n');
      return `{\n${items}\n${spaces}}`;
    }
    return String(obj);
  };

  // Get display content
  const getDisplayContent = (): string => {
    if (!content) return '';

    // Handle raw text
    if (isRawText) {
      return (content as { rawText: string }).rawText;
    }

    // Handle string content
    if (typeof content === 'string') {
      if (prettyJsonEnabled) {
        try {
          const parsed = JSON.parse(content);
          const processedContent = processJsonRecursively(parsed);
          return customJsonStringify(processedContent);
        } catch {
          // If not valid JSON, return as-is
          return content;
        }
      }
      return content;
    }

    // Handle object content
    if (prettyJsonEnabled) {
      const processedContent = processJsonRecursively(content);
      return customJsonStringify(processedContent);
    }

    return JSON.stringify(content, null, 2);
  };

  // Copy content to clipboard
  const handleCopyContent = async () => {
    try {
      const displayContent = getDisplayContent();
      await navigator.clipboard.writeText(displayContent);
      notify('Content copied to clipboard', { type: 'success' });
    } catch {
      notify('Failed to copy content', { type: 'error' });
    }
  };

  const displayContent = getDisplayContent();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight,
      }}
    >
      {showToolbar && content && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            pb: 1,
          }}
        >
          {!isRawText && (
            <FormControlLabel
              control={
                <Switch
                  checked={prettyJsonEnabled}
                  onChange={e => setPrettyJsonEnabled(e.target.checked)}
                  size="small"
                />
              }
              label="JSONify"
            />
          )}
          <Tooltip title="Copy content">
            <IconButton onClick={handleCopyContent} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: content ? '#2d2d2d' : 'transparent',
          borderRadius: 1,
          p: content ? 2 : 0,
        }}
      >
        {content && (
          <SyntaxHighlighter
            language="json"
            style={tomorrow}
            customStyle={{
              margin: 0,
              padding: 0,
              background: 'transparent',
              fontSize: '0.875rem',
              whiteSpace: 'pre',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
              overflow: 'auto',
            }}
            wrapLongLines
            codeTagProps={{
              style: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              },
            }}
          >
            {displayContent}
          </SyntaxHighlighter>
        )}
      </Box>
    </Box>
  );
};
